import { defineStore, storeToRefs } from "pinia";
import { computed, ref, Ref } from "vue";
import {
  getAuth,
  signInWithEmailAndPassword,
  User,
  signOut,
  signInWithCustomToken,
} from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { FBUser, Nullable } from "@/models";
import { app, converter, db, funcs } from "@/firebase";
import router from "@/router";
import { useOperatorAdminStore } from "@/stores/operatorRole";
import { useToast } from "vue-toastification";
import { doc } from "firebase/firestore";
import { useFirestore } from "@vueuse/firebase";
import { useAdminStore } from "./admin";
import { useOperatorStore } from "./operators";
import { useUsersStore } from "./users";
import { useJobStore } from "./jobs";
import * as Sentry from "@sentry/vue";
import { trackSignIn } from "@/helpers/analytics";
import {
  isConnectivityError,
  isExpectedAuthenticationError,
  shouldReportError,
} from "@/helpers/sentryErrors";

const toast = useToast();

export const useAuthStore = defineStore("auth", () => {
  const auth = getAuth(app);
  const user: Ref<Nullable<User>> = ref(null);
  const fbUser: Ref<Nullable<FBUser>> = ref(null);
  const authError = ref("");

  // @ts-ignore
  const versionNumber = PACKAGE_VERSION || "0";

  async function getCurrentUser(): Promise<User | null> {
    return new Promise((resolve, reject) => {
      const unsubscribe = auth.onAuthStateChanged((user) => {
        unsubscribe();
        resolve(user);
      }, reject);
    });
  }

  async function loadUserIntoState(userObj: User) {
    user.value = userObj;
    await getUserData(user.value.uid);
  }

  async function signinUser(email: string, password: string) {
    const normalizedEmail = email.trim();
    authError.value = "";
    if (!normalizedEmail || !password) {
      authError.value = "Email and password are required.";
      throw Object.assign(new Error(authError.value), {
        code: normalizedEmail ? "auth/missing-password" : "auth/invalid-email",
      });
    }

    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        normalizedEmail,
        password
      );
      user.value = userCredential.user;
      trackSignIn("admin_password");
      if (user.value.uid) {
        await getUserData(user.value.uid);
        router.push("/dashboard");
      }
    } catch (error: any) {
      if (shouldReportError(error)) {
        Sentry.captureException(error);
      }
      authError.value = isConnectivityError(error)
        ? "Network error. Please check your connection and try again."
        : error.message;
      throw error;
    }
  }

  const getUserData = async (uid: string) => {
    try {
      const userQuery = doc(db, "users", uid).withConverter(converter<FBUser>())
      const user = useFirestore<FBUser>(userQuery);
      if (user.value) {
        fbUser.value = user.value;
        useAdminStore().setEnvID(user.value.environment);
      }
    } catch (error: any) {
      Sentry.captureException(error);
      authError.value = error.message;
    }
  }

  async function signinOperator(
    usernameOrEmail: string,
    password: string,
    envIdInput?: string
  ) {
    // Validate inputs early - Fixes INTRANSVOICE-8C
    if (!usernameOrEmail?.trim() || !password?.trim()) {
      authError.value = 'Username/email and password are required';
      throw new Error('Username/email and password are required');
    }

    const maxRetries = 2;
    let lastError: Error | null = null;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const operatorAdminStore = useOperatorAdminStore();
        const { envId } = storeToRefs(operatorAdminStore);
        const login = httpsCallable(funcs, "admin-authOperator");
        
        // Determine if input is email or username
        const emailRegex = /\S+@\S+\.\S+/;
        const isEmail = emailRegex.test(usernameOrEmail.trim());
        
        const loginParams = isEmail 
          ? { email: usernameOrEmail.trim(), password, environmentId: envIdInput }
          : { userName: usernameOrEmail.trim(), password, environmentId: envIdInput };
        
        const result = (await login(loginParams)) as {
          data: {
            data: any;
            message: string;
            success: boolean;
          };
        };
        
        if (result.data.success === false) {
          authError.value = result.data.message;
          throw new Error(result.data.message);
        }

        const res = await signInWithCustomToken(auth, result.data.data.token);
        user.value = res.user;
        trackSignIn("operator_password");
        envId.value = result.data.data.environmentId;
        localStorage.setItem('itv_op_uid', res.user.uid);
        localStorage.setItem('itv_op_env', result.data.data.environmentId);
        if (user.value.uid) {
          router.push(`/operator/${envId.value}`);
        }
        return; // Success, exit function
      } catch (error: any) {
        lastError = error;
        const isNetworkError = 
          error.code === 'auth/network-request-failed' ||
          error.message?.includes('network') ||
          error.message?.includes('Failed to fetch');
        
        const isInternalError = error.code === 'internal' || error.message?.includes('internal');

        // Retry on network errors - Fixes INTRANSVOICE-8V, INTRANSVOICE-7B
        if ((isNetworkError || isInternalError) && attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
          continue;
        }

        // Better error messages - Fixes INTRANSVOICE-7B
        if (isNetworkError) {
          authError.value = 'Network error. Please check your connection and try again.';
        } else if (isInternalError) {
          authError.value = 'Server error. Please try again in a moment.';
        } else {
          authError.value = error.message;
        }
        
        if (shouldReportError(error)) {
          Sentry.captureException(error, {
            tags: {
              errorType: 'operator-signin',
              attempt: attempt.toString(),
            },
            extra: {
              isNetworkError,
              isInternalError,
              errorCode: error.code,
            }
          });
        }
        throw error;
      }
    }

    // If we get here, all retries failed
    if (lastError) {
      throw lastError;
    }
  }

  async function signinOperatorWithCode(code: string, envIdInput: string) {
    try {
      const operatorAdminStore = useOperatorAdminStore();
      const { envId } = storeToRefs(operatorAdminStore);
      const { authenticateWithCode } = useOperatorStore();
      
      const result = await authenticateWithCode(code, envIdInput);
      
      if (!result.success) {
        authError.value = result.message;
        throw new Error(result.message);
      }

      const res = await signInWithCustomToken(auth, result.token!);
      user.value = res.user;
      trackSignIn("operator_code");
      envId.value = envIdInput;
      localStorage.setItem('itv_op_uid', res.user.uid);
      localStorage.setItem('itv_op_env', envIdInput);
      if (user.value.uid) {
        router.push(`/operator/${envId.value}`);
      }
    } catch (error: any) {
      if (
        !isExpectedAuthenticationError(error) &&
        shouldReportError(error)
      ) {
        Sentry.captureException(error);
      }
      authError.value = error.message;
    }
  }

  // Email authentication functions
  async function sendVerificationCode(email: string, environmentId: string) {
    try {
      const sendCode = httpsCallable(funcs, "emailAuth-sendVerificationCode");
      const result = await sendCode({ email, environmentId });
      return result.data as { success: boolean; message: string };
    } catch (error: any) {
      Sentry.captureException(error);
      throw new Error(error.message);
    }
  }

  async function verifyEmail(email: string, code: string, environmentId: string) {
    try {
      const verify = httpsCallable(funcs, "emailAuth-verifyEmail");
      const result = await verify({ email, code, environmentId });
      return result.data as { success: boolean; message: string };
    } catch (error: any) {
      Sentry.captureException(error);
      throw new Error(error.message);
    }
  }

  async function checkEmailVerified(email: string, environmentId: string) {
    try {
      const checkVerified = httpsCallable(funcs, "emailAuth-checkEmailVerified");
      const result = await checkVerified({ email, environmentId });
      return result.data as { success: boolean; verified: boolean; message: string };
    } catch (error: any) {
      Sentry.captureException(error);
      throw new Error(error.message);
    }
  }

  async function sendPasswordReset(email: string, environmentId: string) {
    try {
      const sendReset = httpsCallable(funcs, "emailAuth-sendPasswordReset");
      const result = await sendReset({ email, environmentId });
      return result.data as { success: boolean; message: string };
    } catch (error: any) {
      Sentry.captureException(error);
      throw new Error(error.message);
    }
  }

  async function resetPassword(token: string, newPassword: string) {
    try {
      const resetPwd = httpsCallable(funcs, "emailAuth-resetPassword");
      const result = await resetPwd({ token, newPassword });
      return result.data as { success: boolean; message: string };
    } catch (error: any) {
      Sentry.captureException(error);
      throw new Error(error.message);
    }
  }

  const clear = () => {
    user.value = null;
    fbUser.value = null;
    authError.value = "";
  }

  async function signoutUser() {
    signOut(auth)
      .then(() => {
        // Clear stores
        clear();
        localStorage.removeItem('itv_op_uid');
        localStorage.removeItem('itv_op_env');
        useAdminStore().clear();
        useOperatorStore().clear();
        useUsersStore().clear();
        useJobStore().clear();
        return
      
      })
      .catch((error) => {
        Sentry.captureException(error);
        toast.error(`An error occured while signing out: ${error}`);
        throw error;
      });
  }
  
  const authenticatedUser = computed(() => user.value);

  const appVersion = computed(() => {
    if (import.meta.env.DEV) {
      return `dev-${versionNumber}`;
    }
    return versionNumber;
  });

  return {
    auth,
    fbUser,
    appVersion,
    authError,
    authenticatedUser,
    signoutUser,
    getCurrentUser,
    signinUser,
    signinOperator,
    signinOperatorWithCode,
    loadUserIntoState,
    sendVerificationCode,
    verifyEmail,
    checkEmailVerified,
    sendPasswordReset,
    resetPassword,
  };
});
